import { useCallback, useMemo, useState } from 'react';

import { AppBar, Box, Tab, Tabs, Toolbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import {
  selectIsAdvisorAdminOfSelectedCompany,
  selectIsCompanyAdmin,
  selectIsSuperUser,
} from '@/store/slices/userSlice';

import CleanTradeLogoDark from './assets/CleanTrade-logo-dark.png';
import CleanTradeLogoLight from './assets/CleanTrade-logo-light.png';
import { HelpPopover } from './components/HelpPopover';
import { CleanSight } from './components/cleanSight/CleanSight';
import UserMenu from './components/userMenu/UserMenu';
import { CompanyRole, RoutePath } from './utils/enums';

import {
  StyledLogoImage,
  StyledLogoLink,
  appBarSx,
  leftNavSx,
  navItemsContainerSx,
  rightNavSx,
  spacerSx,
} from './Navigation.styles';

enum NAVIGATION_ITEMS {
  DASHBOARD = 'dashboard',
  MARKET_ACTIVITY = 'market-activity',
  MARKET_DEPTH = 'market-depth',
  TRANSACTIONS = 'transactions',
  ADMIN = 'admin',
}

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null);
  const selectedCompany = useAppSelector(state => state.user.selectedCompany);
  const isUserSuperUser = useAppSelector(selectIsSuperUser);
  const isUserCompanyAdmin = useAppSelector(selectIsCompanyAdmin);
  const isUserAdvisorAdminOfSelectedCompany = useAppSelector(
    selectIsAdvisorAdminOfSelectedCompany
  );
  const { darkMode } = useAppSelector(state => state.common);

  const CleanTradeLogo = darkMode ? CleanTradeLogoDark : CleanTradeLogoLight;

  const menuItems = useMemo(
    () => [
      {
        id: NAVIGATION_ITEMS.DASHBOARD,
        label: 'Dashboard',
        path: RoutePath.DASHBOARD,
      },
      {
        id: NAVIGATION_ITEMS.MARKET_ACTIVITY,
        label: 'Market Activity',
        path: RoutePath.MARKET_ACTIVITY,
      },
      {
        id: NAVIGATION_ITEMS.MARKET_DEPTH,
        label: 'Market Depth',
        path: RoutePath.MARKET_DEPTH,
      },
      {
        id: NAVIGATION_ITEMS.TRANSACTIONS,
        label: 'Transactions',
        path: RoutePath.TRANSACTIONS,
      },
      {
        id: NAVIGATION_ITEMS.ADMIN,
        label: 'Admin',
        path: RoutePath.ADMIN,
      },
    ],
    []
  );

  const getNavigationItems = useCallback(() => {
    // everything for user supers
    if (isUserSuperUser) {
      return menuItems;
    }

    // company admins
    if (isUserCompanyAdmin) {
      // if user is admin of the selected advisor company, only admin page
      if (isUserAdvisorAdminOfSelectedCompany) {
        return menuItems.filter(item => item.id === NAVIGATION_ITEMS.ADMIN);
      }

      // ...otherwise everything
      return menuItems;
    }

    // if advisor company selected for regular user, nothing (only user settings available)
    if (selectedCompany?.companyRole === CompanyRole.ADVISOR) {
      return [];
    }

    // for regular users everything, except admin page
    return menuItems.filter(item => item.id !== NAVIGATION_ITEMS.ADMIN);
  }, [
    menuItems,
    isUserAdvisorAdminOfSelectedCompany,
    isUserCompanyAdmin,
    isUserSuperUser,
    selectedCompany?.companyRole,
  ]);

  const handleHelpOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpClose = () => {
    setHelpAnchorEl(null);
  };

  const getNavigationTabValueByPath = () =>
    getNavigationItems().some(item => item.path === location.pathname)
      ? location.pathname
      : false;

  const navigationTabsValue = location.pathname.startsWith(RoutePath.ADMIN)
    ? RoutePath.ADMIN
    : getNavigationTabValueByPath();

  return (
    <AppBar position="sticky" id="navAppBar" sx={appBarSx} enableColorOnDark>
      <Toolbar variant="dense" id="navToolBar">
        {/* Left side - Logo and Navigation */}
        <Box sx={leftNavSx} id="leftNav">
          <StyledLogoLink to="/">
            <StyledLogoImage src={CleanTradeLogo} alt="CleanTrade Logo" />
          </StyledLogoLink>

          <Box sx={navItemsContainerSx}>
            <Tabs
              value={navigationTabsValue}
              onChange={(_, newUrl) => navigate(newUrl)}
              aria-label="Navigation"
            >
              {getNavigationItems().map(item => (
                <Tab key={item.id} label={item.label} value={item.path} />
              ))}

              <Tab
                label="Help"
                onClick={handleHelpOpen}
                aria-haspopup="true"
                aria-expanded={Boolean(helpAnchorEl)}
              />
            </Tabs>
          </Box>
        </Box>

        {/* Space between left and right side */}
        <Box sx={spacerSx} />

        {/* Right side - User info and settings */}
        <Box sx={rightNavSx} id="rightNav">
          {/* CleanSight */}
          <CleanSight />
          {/* User Settings Menu */}
          <UserMenu />
        </Box>
      </Toolbar>

      <HelpPopover anchorEl={helpAnchorEl} onClose={handleHelpClose} />
    </AppBar>
  );
}
